<template lang="pug">
.profile-collection-expanded-panel
  .panel-header
    div
      p.panel-title
        | {{ collection.title }}
        span.archived-badge(v-if="collection.status === 'ARCHIVED'") Archived
      p.panel-owner(v-if="collection.ownerName") {{ collection.ownerName }}
    button.panel-close(type="button" aria-label="Close" @click="$emit('close')")
      img(src="@/assets/icons/close.svg" alt="")

  p.panel-status {{ statusText }}
  p.panel-archived-note(v-if="collection.status === 'ARCHIVED'")
    | This collection has ended. Its codes can no longer be redeemed.

  sticker-collection-detail-tabs(
    v-model="activeSubTab"
    :collection="collection"
    layout="panel"
    @select-sticker="onStickerClick"
  )
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { useRouter } from 'vue-router';
import StickerCollectionDetailTabs from '@/views/web-app/sticker-collection/components/StickerCollectionDetailTabs.vue';
import {
  completedCollectionText,
  completionGoalText,
  remainingToCompleteText,
} from '@/utils/stickerCollectionProgress';
import type {
  MyStickerCollection,
  StickerCollectionStickerRef,
} from '@/data/stickerCollection.types';

export default defineComponent({
  name: 'ProfileCollectionExpandedPanel',
  components: { StickerCollectionDetailTabs },
  props: {
    collection: {
      type: Object as () => MyStickerCollection,
      required: true,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const router = useRouter();
    const activeSubTab = ref<'stickers' | 'updates'>('stickers');

    const statusText = computed(() => {
      if (props.collection.isCompleted) {
        if (props.collection.showProgress === false) return 'Collection complete!';
        return completedCollectionText(props.collection);
      }
      if (props.collection.showProgress === false) return completionGoalText(props.collection);
      return remainingToCompleteText(props.collection);
    });

    function onStickerClick(sticker: StickerCollectionStickerRef) {
      const slugOrId = sticker.slug || sticker.id;
      emit('close');
      router.push(`/web/sticker/${slugOrId}`);
    }

    return {
      activeSubTab,
      statusText,
      onStickerClick,
    };
  },
});
</script>

<style lang="scss" scoped>
.profile-collection-expanded-panel {
  margin-top: 1.25rem;
  background: #f9fafb;
  border-radius: 1rem;
  overflow: hidden;

  :deep(.detail-tab-bar) {
    padding: 0 1rem;
  }

  :deep(.detail-tab-body) {
    padding: 1rem;
  }
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1rem 0.75rem;
}

.panel-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 900;
  color: #1a2744;
}

.panel-owner {
  margin: 0.25rem 0 0;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #0d9488;
}

.panel-close {
  border: none;
  background: transparent;
  padding: 0.25rem;
  cursor: pointer;
  opacity: 0.35;
  margin-top: 0.125rem;

  img {
    width: 0.875rem;
    height: 0.875rem;
  }

  &:hover {
    opacity: 0.6;
  }
}

.panel-status {
  margin: 0;
  padding: 0 1rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
}

.panel-title .archived-badge {
  margin-left: 0.375rem;
  vertical-align: middle;
}

.panel-archived-note {
  margin: -0.25rem 0 0;
  padding: 0 1rem 0.75rem;
  font-size: 0.6875rem;
  color: #6b7280;
}

.archived-badge {
  display: inline-block;
  padding: 0.0625rem 0.375rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 0.5625rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
</style>
